import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CronExpressionPicker from '../../components/forms/CronExpressionPicker';
import {
  OperationalDaysClient,
  OperationalDayTemplateDto,
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
} from '../../app/services/api/tasksClient';

const operationalDaysClient = OperationalDaysClient;
const templatesClient = TaskTemplatesClient;
const prioritiesClient = TaskPrioritiesClient;

/** Task entry within an operational day - cron-based schedule */
interface OperationalDayTask {
  id: number;
  templateId: number | '';
  cronExpression: string; // Quartz cron expression for scheduling
  cronSummary: string; // Human-readable schedule summary
  assignedPersonaId: number | null; // role to assign to, or null for unassigned
  assignedUserId: number | null; // specific user, or null
  overridePriorityId: number | null; // optional priority override
  requiresApproval: boolean; // whether task requires approval upon completion
  approverPersonaId: number | null; // role that can approve, or null
  approverUserId: number | null; // specific user that can approve, or null
}

/** Location assignment for the operational day as a whole */
interface OperationalDayLocationAssignment {
  id: number;
  franchiseId: number;
  storeNumbers: string[]; // empty = all stores in franchise
}

const OperationalDayDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo, franchises, stores } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const canCreate = hasPermissionTo ? hasPermissionTo(['OperationalDaysCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['OperationalDaysEdit']) : false;

  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);

  const [day, setDay] = useState<OperationalDayTemplateDto | null>(null);
  const [formValues, setFormValues] = useState<{
    name: string;
    description: string;
    effectiveFromDate: string;
    effectiveToDate: string;
    isActive: boolean;
    cronExpression: string;
  }>({
    name: '',
    description: '',
    effectiveFromDate: '',
    effectiveToDate: '',
    isActive: true,
    cronExpression: '0 0 9 ? * MON-FRI *', // Default: 9 AM weekdays
  });

  // Tasks within this operational day
  const [tasks, setTasks] = useState<OperationalDayTask[]>([]);
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<OperationalDayTask | null>(null);
  const [deleteTaskIndex, setDeleteTaskIndex] = useState<number | null>(null);

  // Location assignments for the operational day as a whole
  const [locationAssignments, setLocationAssignments] = useState<OperationalDayLocationAssignment[]>([]);
  const [editingLocationIndex, setEditingLocationIndex] = useState<number | null>(null);
  const [editingLocation, setEditingLocation] = useState<OperationalDayLocationAssignment | null>(null);
  const [deleteLocationIndex, setDeleteLocationIndex] = useState<number | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fake personas for role assignment
  const fakePersonas: { id: number; name: string }[] = [
    { id: 1, name: 'Store Manager' },
    { id: 2, name: 'Assistant Manager' },
    { id: 3, name: 'Team Member' },
    { id: 4, name: 'Shift Supervisor' },
  ];

  // Fake users for approver selection
  const fakeUsers: { id: number; name: string }[] = [
    { id: 1001, name: 'Alex Smith' },
    { id: 1002, name: 'Jordan Lee' },
    { id: 1003, name: 'Taylor Brown' },
    { id: 1004, name: 'Morgan Davis' },
    { id: 1005, name: 'Casey Wilson' },
  ];

  const getPersonaName = (personaId: number | null) => {
    if (personaId == null) return null;
    return fakePersonas.find(p => p.id === personaId)?.name ?? null;
  };

  useEffect(() => {
    templatesClient.getTemplates().then(setTemplates).catch(() => setTemplates([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));

    if (!isCreate && id) {
      const dayId = Number(id);
      operationalDaysClient.getOperationalDayById(dayId).then(r => {
        const d = r.data;
        setDay(d);
        setFormValues({
          name: d.name,
          description: d.description ?? '',
          effectiveFromDate: d.effectiveFromDate?.substring(0, 10) ?? '',
          effectiveToDate: d.effectiveToDate?.substring(0, 10) ?? '',
          isActive: d.isActive,
          cronExpression: d.cronExpression ?? '0 0 9 ? * MON-FRI *',
        });
        // Tasks and location assignments would normally come from API; keep local for now.
      }).catch(() => setDay(null));
    }
  }, [id, isCreate]);

  if (isCreate && !canCreate) {
    return (
      <PageArea>
        <PageTitle>Operational Day</PageTitle>
        <Pill text='You do not have permission to create operational days.' />
      </PageArea>
    );
  }

  if (!isCreate && !canEdit) {
    return (
      <PageArea>
        <PageTitle>Operational Day</PageTitle>
        <Pill text='You do not have permission to edit operational days.' />
      </PageArea>
    );
  }

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.name.trim()) e.name = 'Required';
    if (!formValues.effectiveFromDate) e.effectiveFromDate = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const dto: OperationalDayTemplateDto = {
      id: day?.id ?? 0,
      isActive: formValues.isActive,
      name: formValues.name.trim(),
      description: formValues.description || null,
      effectiveFromDate: formValues.effectiveFromDate,
      effectiveToDate: formValues.effectiveToDate || null,
    };

    const payload = {
      name: dto.name,
      description: dto.description,
      effectiveFromDate: dto.effectiveFromDate,
      effectiveToDate: dto.effectiveToDate,
      isActive: dto.isActive,
      cronExpression: formValues.cronExpression,
      // Future: include tasks and locationAssignments when API supports it
    };

    if (isCreate) {
      await operationalDaysClient.createOperationalDay(payload);
    } else if (day) {
      await operationalDaysClient.updateOperationalDay(day.id, payload);
    }

    navigate('/operational-days');
  };

  const title = isCreate ? 'Create Operational Day' : 'Edit Operational Day';

  // Task handlers
  const handleAddTask = () => {
    const base: OperationalDayTask = {
      id: 0,
      templateId: '',
      cronExpression: '0 0 9 ? * * *', // Default: 9 AM daily
      cronSummary: 'At 09:00 AM',
      assignedPersonaId: null,
      assignedUserId: null,
      overridePriorityId: null,
      requiresApproval: false,
      approverPersonaId: null,
      approverUserId: null,
    };
    setEditingTaskIndex(null);
    setEditingTask(base);
  };

  const handleEditTask = (index: number) => {
    const existing = tasks[index];
    if (!existing) return;
    setEditingTaskIndex(index);
    setEditingTask({ ...existing });
  };

  const handleTaskFieldChange = <K extends keyof OperationalDayTask>(field: K, value: OperationalDayTask[K]) => {
    setEditingTask(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveTask = () => {
    if (!editingTask) return;
    setTasks(prev => {
      if (editingTaskIndex === null) {
        return [...prev, { ...editingTask, id: prev.length + 1 }];
      }
      return prev.map((t, i) => (i === editingTaskIndex ? editingTask : t));
    });
    setEditingTask(null);
    setEditingTaskIndex(null);
  };

  const handleCancelTaskEdit = () => {
    setEditingTask(null);
    setEditingTaskIndex(null);
  };

  const handleConfirmDeleteTask = () => {
    if (deleteTaskIndex === null) return;
    setTasks(prev => prev.filter((_, i) => i !== deleteTaskIndex));
    setDeleteTaskIndex(null);
  };

  // Location assignment handlers
  const handleAddLocation = () => {
    const base: OperationalDayLocationAssignment = {
      id: 0,
      franchiseId: franchises[0]?.franchiseId ?? 0,
      storeNumbers: [],
    };
    setEditingLocationIndex(null);
    setEditingLocation(base);
  };

  const handleEditLocation = (index: number) => {
    const existing = locationAssignments[index];
    if (!existing) return;
    setEditingLocationIndex(index);
    setEditingLocation({ ...existing });
  };

  const handleLocationFieldChange = <K extends keyof OperationalDayLocationAssignment>(field: K, value: OperationalDayLocationAssignment[K]) => {
    setEditingLocation(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveLocation = () => {
    if (!editingLocation) return;
    setLocationAssignments(prev => {
      if (editingLocationIndex === null) {
        return [...prev, { ...editingLocation, id: prev.length + 1 }];
      }
      return prev.map((l, i) => (i === editingLocationIndex ? editingLocation : l));
    });
    setEditingLocation(null);
    setEditingLocationIndex(null);
  };

  const handleCancelLocationEdit = () => {
    setEditingLocation(null);
    setEditingLocationIndex(null);
  };

  const handleConfirmDeleteLocation = () => {
    if (deleteLocationIndex === null) return;
    setLocationAssignments(prev => prev.filter((_, i) => i !== deleteLocationIndex));
    setDeleteLocationIndex(null);
  };

  return (
    <PageArea>
      <Grid
        container
        spacing={5}
      >
        <Grid
          item
          xs={12}
        >
          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid item>
              <PageTitle>{title}</PageTitle>
            </Grid>
            <Grid item>
              <Grid container
                spacing={2}>
                <Grid item>
                  <Button variant='secondary'
                    onClick={() => navigate('/operational-days')}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant='primary'
                    onClick={handleSave}>
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Section style={{ paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }}>
            <Grid
              container
              spacing={3}
              alignItems='center'
            >
              <Grid
                item
                xs={12}
              >
                <h3>Headline detail</h3>
              </Grid>
            </Grid>

            <Grid container
              spacing={3}>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  fullWidth
                  size='small'
                  label='Name'
                  value={formValues.name}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    name: e.target.value,
                  }))}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>

              <Grid item
                xs={12}
                md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.isActive}
                      onChange={e => setFormValues(v => ({
                        ...v,
                        isActive: e.target.checked,
                      }))}
                    />
                  }
                  label='Operational day is active'
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  size='small'
                  label='Effective from'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={formValues.effectiveFromDate}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    effectiveFromDate: e.target.value,
                  }))}
                  error={!!errors.effectiveFromDate}
                  helperText={errors.effectiveFromDate}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  size='small'
                  label='Effective to'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={formValues.effectiveToDate}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    effectiveToDate: e.target.value,
                  }))}
                />
              </Grid>

              <Grid item
                xs={12}>
                <TextField
                  fullWidth
                  size='small'
                  label='Description'
                  value={formValues.description}
                  multiline
                  minRows={2}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    description: e.target.value,
                  }))}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}>
          <Section style={{ paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }}>
            <Grid
              container
              spacing={3}
              alignItems='center'
            >
              <Grid
                item
                xs={12}
              >
                <h3>Schedule</h3>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={3}>
              <Grid
                item
                xs={12}>
                <Typography
                  variant="textSM"
                  color="textSecondary"
                  sx={{ mb: 2 }}>
                  Define when this operational day applies using a schedule pattern. Specify which days of the week, months, and years this operational day should be active.
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}>
                <CronExpressionPicker
                  value={formValues.cronExpression}
                  onChange={(newCron) => setFormValues(v => ({
                    ...v,
                    cronExpression: newCron,
                  }))}
                  hideTimeFields={true}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Tasks Section */}
        <Grid item
          xs={12}>
          <Section>
            <Grid
              container
              justifyContent='space-between'
              alignItems='center'
              style={{ marginBottom: 8 }}
            >
              <Grid item>
                <TableHeader>Tasks</TableHeader>
              </Grid>
              <Grid item>
                <Button
                  variant='secondary'
                  size="md"
                  onClick={handleAddTask}
                  sx={{ marginRight: 2 }}
                >
                  Add task
                </Button>
              </Grid>
            </Grid>

            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Assigned Role</TableCell>
                  <TableCell>Priority Override</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => {
                  const templateName = templates.find(t => t.id === task.templateId)?.name || '-';
                  const personaName = getPersonaName(task.assignedPersonaId);
                  const priorityName = priorities.find(p => p.id === task.overridePriorityId)?.name || '-';

                  return (
                    <TableRow key={task.id}>
                      <TableCell>{templateName}</TableCell>
                      <TableCell>{task.cronSummary || task.cronExpression}</TableCell>
                      <TableCell>{personaName}</TableCell>
                      <TableCell>{priorityName}</TableCell>
                      <TableCell align='right'>
                        <IconButton size='small'
                          onClick={() => handleEditTask(index)}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton size='small'
                          onClick={() => setDeleteTaskIndex(index)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}
                      style={{ textAlign: 'center' }}>
                      No tasks assigned.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Section>
        </Grid>

        {/* Location Assignments Section */}
        <Grid item
          xs={12}>
          <Section>
            <Grid
              container
              justifyContent='space-between'
              alignItems='center'
              style={{ marginBottom: 8 }}
            >
              <Grid item>
                <TableHeader>Location Assignments</TableHeader>
              </Grid>
              <Grid item>
                <Button
                  variant='secondary'
                  onClick={handleAddLocation}
                  size="md"
                  sx={{ marginRight: 2 }}
                >
                  Add location
                </Button>
              </Grid>
            </Grid>

            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Franchise</TableCell>
                  <TableCell>Stores</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locationAssignments.map((loc, index) => {
                  const franchiseName = franchises.find(f => f.franchiseId === loc.franchiseId)?.franchiseName || '-';
                  const storeDisplay = loc.storeNumbers.length > 0 ? loc.storeNumbers.join(', ') : 'All stores';

                  return (
                    <TableRow key={loc.id}>
                      <TableCell>{franchiseName}</TableCell>
                      <TableCell>{storeDisplay}</TableCell>
                      <TableCell align='right'>
                        <IconButton size='small'
                          onClick={() => handleEditLocation(index)}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton size='small'
                          onClick={() => setDeleteLocationIndex(index)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {locationAssignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}
                      style={{ textAlign: 'center' }}>
                      No location assignments.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Section>
        </Grid>
      </Grid>

      {/* Task Edit Dialog */}
      <Dialog open={!!editingTask}
        onClose={handleCancelTaskEdit}
        maxWidth='md'
        fullWidth>
        <DialogTitle>{editingTaskIndex === null ? 'Add Task' : 'Edit Task'}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={2}
            sx={{ mt: 1 }}>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label='Template'
                value={editingTask?.templateId ?? ''}
                onChange={e => handleTaskFieldChange('templateId', e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value=''>Select template</MenuItem>
                {templates.map(t => (
                  <MenuItem key={t.id}
                    value={t.id}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <Typography
                variant='textMD'
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Schedule: {editingTask?.cronSummary || 'Configure below'}
              </Typography>
              <CronExpressionPicker
                value={editingTask?.cronExpression ?? '0 0 9 ? * * *'}
                onChange={(cron: string) => handleTaskFieldChange('cronExpression', cron)}
                onHumanReadableChange={(text: string) => handleTaskFieldChange('cronSummary', text)}
                disableDate={true}
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label='Assigned Role'
                value={editingTask?.assignedPersonaId ?? ''}
                onChange={e => handleTaskFieldChange('assignedPersonaId', e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value=''>No role assigned</MenuItem>
                {fakePersonas.map(p => (
                  <MenuItem key={p.id}
                    value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label='Priority Override'
                value={editingTask?.overridePriorityId ?? ''}
                onChange={e => handleTaskFieldChange('overridePriorityId', e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value=''>Use template default</MenuItem>
                {priorities.map(p => (
                  <MenuItem key={p.id}
                    value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Approval Settings Section */}
            <Grid
              item
              xs={12}
            >
              <Typography
                variant='textSM'
                sx={{ fontWeight: 600, color: 'text.secondary', mt: 2, mb: 1 }}
              >
                Approval Settings
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingTask?.requiresApproval ?? false}
                    onChange={e => {
                      handleTaskFieldChange('requiresApproval', e.target.checked);
                      if (!e.target.checked) {
                        // Clear approver when approval not required
                        handleTaskFieldChange('approverPersonaId', null);
                        handleTaskFieldChange('approverUserId', null);
                      }
                    }}
                  />
                }
                label='Requires Approval'
              />
            </Grid>
            {editingTask?.requiresApproval && (
              <>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextField
                    select
                    fullWidth
                    size='small'
                    label='Approver Role'
                    value={editingTask?.approverPersonaId ?? ''}
                    onChange={e => {
                      handleTaskFieldChange('approverPersonaId', e.target.value === '' ? null : Number(e.target.value));
                      // Clear user if role selected
                      if (e.target.value !== '') {
                        handleTaskFieldChange('approverUserId', null);
                      }
                    }}
                    disabled={!!editingTask?.approverUserId}
                  >
                    <MenuItem value=''>No role selected</MenuItem>
                    {fakePersonas.map(p => (
                      <MenuItem
                        key={p.id}
                        value={p.id}
                      >
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextField
                    select
                    fullWidth
                    size='small'
                    label='Or Specific Approver'
                    value={editingTask?.approverUserId ?? ''}
                    onChange={e => {
                      handleTaskFieldChange('approverUserId', e.target.value === '' ? null : Number(e.target.value));
                      // Clear role if user selected
                      if (e.target.value !== '') {
                        handleTaskFieldChange('approverPersonaId', null);
                      }
                    }}
                    disabled={!!editingTask?.approverPersonaId}
                  >
                    <MenuItem value=''>No user selected</MenuItem>
                    {fakeUsers.map(u => (
                      <MenuItem
                        key={u.id}
                        value={u.id}
                      >
                        {u.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTaskEdit}>Cancel</Button>
          <Button variant='primary'
            onClick={handleSaveTask}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Task Confirmation */}
      <Dialog open={deleteTaskIndex !== null}
        onClose={() => setDeleteTaskIndex(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this task?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskIndex(null)}>Cancel</Button>
          <Button color='error'
            onClick={handleConfirmDeleteTask}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Location Edit Dialog */}
      <Dialog open={!!editingLocation}
        onClose={handleCancelLocationEdit}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>{editingLocationIndex === null ? 'Add Location Assignment' : 'Edit Location Assignment'}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={2}
            sx={{ mt: 1 }}>
            <Grid item
              xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label='Franchise'
                value={editingLocation?.franchiseId ?? ''}
                onChange={e => handleLocationFieldChange('franchiseId', Number(e.target.value))}
              >
                {franchises.map(f => (
                  <MenuItem key={f.franchiseId}
                    value={f.franchiseId}>{f.franchiseName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <Autocomplete
                multiple
                size='small'
                options={stores
                  .filter(s => s.franchiseId === editingLocation?.franchiseId)
                  .map(s => s.storeNumber)
                  .filter((s): s is string => s !== undefined)}
                value={editingLocation?.storeNumbers ?? []}
                onChange={(_, newValue) => handleLocationFieldChange('storeNumbers', newValue.filter((v): v is string => v !== undefined))}
                renderInput={(params) => (
                  <TextField {...params}
                    label='Stores (leave empty for all)' />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLocationEdit}>Cancel</Button>
          <Button variant='primary'
            onClick={handleSaveLocation}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Location Confirmation */}
      <Dialog open={deleteLocationIndex !== null}
        onClose={() => setDeleteLocationIndex(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this location assignment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLocationIndex(null)}>Cancel</Button>
          <Button color='error'
            onClick={handleConfirmDeleteLocation}>Delete</Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default OperationalDayDetails;
